import { Button as HeadlessButton } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useId } from 'react';
import { twJoin } from 'tailwind-merge';
import { cn } from '../../lib/classNameHelpers';
import { Input } from '../catalyst-ui-kit/input';
import { useMultiSelectContext } from './MultiSelectContext';

const MAX_ICONS = 5;

export function MultiSelectTrigger() {
  const {
    isOpen,
    disabled,
    triggerLabel,
    hideChevron,
    buttonClassName,
    selectedOptions,
    triggerContent,
    showSearchInput,
    searchQuery,
    setSearchQuery,
    searchInputRef,
    triggerTextRef,
    triggerWidth,
    handleSearchInputKeyDown,
    getReferenceProps,
    refs,
    triggerComponent: TriggerComponent,
  } = useMultiSelectContext();

  const triggerLabelId = useId();

  const selectedOptionsWithIcons = selectedOptions
    .filter((option) => option.icon)
    .slice(0, MAX_ICONS);

  const commonProps = {
    ref: refs.setReference,
    disabled,
    title: triggerLabel,
    ...getReferenceProps({
      'aria-disabled': disabled,
      'aria-labelledby': triggerLabelId,
      'aria-expanded': isOpen,
      'aria-controls': isOpen ? 'select-options' : undefined,
      'aria-haspopup': 'listbox',
      'aria-autocomplete': 'list',
      role: 'combobox',
    }),
  };

  return TriggerComponent ? (
    <TriggerComponent {...commonProps} isOpen={isOpen} />
  ) : (
    <HeadlessButton
      {...commonProps}
      className={cn(
        'flex w-full transition-colors cursor-pointer items-center text-xs rounded-md justify-between gap-2 px-3 py-2.5',
        // Border styles
        'border border-backgroundTertiary hover:border-backgroundHoverOverlay',
        // Background styles
        'bg-backgroundTertiary',
        disabled && 'cursor-not-allowed pointer-events-none opacity-50',
        isOpen && '!border-contentBlue',
        buttonClassName
      )}
    >
      <div className="flex flex-1 items-center">
        <div className="flex w-full items-center gap-2 font-normal">
          <span id={triggerLabelId} className="sr-only">
            {triggerLabel}
          </span>
          {selectedOptionsWithIcons.length > 0 && (
            <div
              className={twJoin('flex items-center', selectedOptions.length > 1 ? '-m-1 mr-0' : '')}
            >
              {selectedOptionsWithIcons.map(
                (option, index) =>
                  option.icon && (
                    <div
                      key={option.label ?? String(option.value)}
                      className={twJoin(
                        'flex shrink-0 items-center justify-center overflow-hidden',
                        index > 0 ? (selectedOptions.length > 3 ? '-ml-3' : '-ml-4') : '',
                        selectedOptions.length > 1
                          ? 'size-7 rounded-full border border-borderPrimary bg-backgroundPrimary p-0.5'
                          : 'size-5',
                        selectedOptions.length > 3 ? '!size-6' : ''
                      )}
                      style={{
                        zIndex: MAX_ICONS - index,
                      }}
                    >
                      {option.icon}
                    </div>
                  )
              )}
            </div>
          )}
          {isOpen && !showSearchInput ? (
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              ref={searchInputRef}
              placeholder={triggerContent}
              containerClassName="w-full border-none !bg-transparent p-0"
              className={twJoin(
                '!border-none !p-0 text-[length:inherit] leading-[inherit] !outline-none placeholder:!text-[length:inherit] placeholder:!text-contentPrimary',
                searchQuery.length > 0 ? 'cursor-text' : 'cursor-pointer'
              )}
              style={{
                width: triggerWidth ? `${triggerWidth - 0.5}px` : '',
              }}
              onKeyDown={handleSearchInputKeyDown}
              onClick={(e) => {
                if (searchQuery.length) {
                  e.stopPropagation();
                }
              }}
              onMouseDown={(e) => {
                if (!searchQuery.length) {
                  e.preventDefault();
                }
              }}
            />
          ) : null}
          {triggerContent && (
            <span
              ref={triggerTextRef}
              className={twJoin(
                'w-full truncate text-left text-[length:inherit] leading-[inherit]',
                isOpen && !showSearchInput ? 'absolute opacity-0' : ''
              )}
            >
              {triggerContent}
            </span>
          )}
        </div>
      </div>
      {!hideChevron && (
        <ChevronDownIcon
          className={twJoin(
            'size-3 shrink-0 fill-contentSecondary transition-transform',
            isOpen ? 'rotate-180' : ''
          )}
          aria-hidden="true"
        />
      )}
    </HeadlessButton>
  );
}
